import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const regions = [
  {
    id: "us",
    name: "United States",
    flag: "https://flagcdn.com/w80/us.png",
    active: true,
    vendor: "JM Bullion",
    vendorPath: "/retailers/jm-bullion",
    logo: "/jm-logo.svg",
  },
  {
    id: "ca",
    name: "Canada",
    flag: "https://flagcdn.com/w80/ca.png",
    active: true,
    vendor: "TD Precious Metals",
    vendorPath: "/retailers/td",
    logo: "/td-logo.svg",
  },
  {
    id: "uk",
    name: "United Kingdom",
    flag: "https://flagcdn.com/w80/gb.png",
    active: false,
    vendor: null,
    vendorPath: null,
  },
  {
    id: "au",
    name: "Australia",
    flag: "https://flagcdn.com/w80/au.png",
    active: false,
    vendor: null,
    vendorPath: null,
  },
  {
    id: "eu",
    name: "European Union",
    flag: "https://flagcdn.com/w80/eu.png",
    active: false,
    vendor: null,
    vendorPath: null,
  },
];

// Language options for future multilingual support
const languages = [
  {
    id: "zh",
    name: "Chinese",
    // Combined flag representing Chinese-speaking regions (China, Taiwan, HK, Macau, Singapore)
    flag: "/images/flags/chinese-lang.png",
    active: false,
    label: "中文",
  },
  {
    id: "fr",
    name: "French",
    // Quebec Fleur De Lis flag
    flag: "/images/flags/quebec.png",
    active: false,
    label: "Français",
  },
];

export const RegionGateway = () => {
  const [selectedRegion, setSelectedRegion] = useState("us");

  return (
    <div 
      className="region-gateway py-2 md:py-2.5 bg-black/95 backdrop-blur-sm"
      data-testid="region-gateway"
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col items-center justify-center gap-2 md:gap-3">
          <p className="ticker-text text-center text-[0.55rem] md:text-[0.65rem] hidden md:block">
            Goldnote sells through select online retailers; please click your region here:
          </p>
          
          <div className="flex items-center gap-3 md:gap-5">
            <TooltipProvider delayDuration={0}>
              {regions.map((region) => (
                <Tooltip key={region.id}>
                  <TooltipTrigger asChild>
                    {region.active && region.vendorPath ? (
                      <Link
                        to={region.vendorPath}
                        onClick={() => setSelectedRegion(region.id)}
                        className={`flex flex-col items-center gap-0.5 group`}
                        data-testid={`flag-${region.id}`}
                        aria-label={region.name}
                      >
                        <div className={`flag-button ${selectedRegion === region.id ? 'active' : ''}`}>
                          <img 
                            src={region.flag} 
                            alt={region.name}
                            loading="eager"
                            decoding="async"
                            className="w-5 h-3 md:w-6 md:h-4 object-cover rounded-sm"
                          />
                        </div>
                        <span className="text-[0.4rem] md:text-[0.45rem] tracking-wider uppercase text-[#D4AF37]/70 group-hover:text-[#D4AF37] transition-colors whitespace-nowrap">
                          {region.vendor}
                        </span>
                        {region.logo && (
                          <img 
                            src={region.logo}
                            alt={`${region.vendor} logo`}
                            loading="eager"
                            decoding="async"
                            className="h-4 md:h-5 w-auto max-w-[35px] md:max-w-[45px] object-contain opacity-90 group-hover:opacity-100 transition-opacity"
                          />
                        )}
                      </Link>
                    ) : (
                      <div 
                        className="flex flex-col items-center gap-0.5 opacity-25 cursor-not-allowed hidden md:flex"
                        data-testid={`flag-${region.id}`}
                      >
                        <div className="flag-button grayscale opacity-50">
                          <img 
                            src={region.flag} 
                            alt={region.name}
                            loading="eager"
                            decoding="async"
                            className="w-4 h-2.5 md:w-5 md:h-3 object-cover rounded-sm"
                          />
                        </div>
                        <span className="text-[0.35rem] md:text-[0.4rem] tracking-wider uppercase text-white/20 whitespace-nowrap">
                          Soon
                        </span>
                      </div>
                    )}
                  </TooltipTrigger>
                  <TooltipContent 
                    side="bottom" 
                    className="bg-[#0A0A0A] border border-[#B08D1A] text-[#D4AF37] px-3 py-2 hidden md:block"
                  >
                    <p className="text-xs tracking-wider uppercase">
                      {region.active ? `${region.name} - ${region.vendor}` : `${region.name} - Coming Soon`}
                    </p>
                  </TooltipContent>
                </Tooltip>
              ))}
              
              {/* Separator */}
              <div className="w-[1px] h-6 bg-white/10 mx-1 hidden md:block" />
              
              {/* Language Options - Faded/Coming Soon */}
              {languages.map((lang) => (
                <Tooltip key={lang.id}>
                  <TooltipTrigger asChild>
                    <div 
                      className="flex flex-col items-center gap-0.5 opacity-30 cursor-not-allowed hidden md:flex"
                      data-testid={`lang-${lang.id}`}
                    >
                      <div className="flag-button grayscale opacity-50">
                        <img 
                          src={lang.flag} 
                          alt={lang.name}
                          loading="eager"
                          decoding="async"
                          className="w-4 h-2.5 md:w-5 md:h-3 object-cover rounded-sm"
                          onError={(e) => {
                            // Fallback if custom flag not found
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                      <span className="text-[0.35rem] md:text-[0.4rem] tracking-wider uppercase text-white/20 whitespace-nowrap">
                        {lang.label}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent 
                    side="bottom" 
                    className="bg-[#0A0A0A] border border-[#B08D1A] text-[#D4AF37] px-3 py-2 hidden md:block"
                  >
                    <p className="text-xs tracking-wider uppercase">
                      {lang.name} - Coming Soon
                    </p>
                  </TooltipContent>
                </Tooltip>
              ))}
              
              {/* Separator */}
              <div className="w-[1px] h-6 bg-[#D4AF37]/30 mx-2 hidden md:block" />
              
              {/* Language Selector Dropdown - Seamless Translation */}
              <div className="flex items-center gap-2" data-testid="translate-container">
                <svg className="w-4 h-4 text-[#D4AF37] hidden md:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                <select 
                  className="translate-select bg-[#0A0A0A] text-[#D4AF37] border border-[#D4AF37]/50 rounded px-3 py-1.5 text-xs cursor-pointer outline-none hover:border-[#D4AF37] transition-colors"
                  onChange={(e) => {
                    if (e.target.value) {
                      // Set Google Translate cookie for seamless translation
                      document.cookie = `googtrans=/en/${e.target.value};path=/;`;
                      document.cookie = `googtrans=/en/${e.target.value};path=/;domain=${window.location.hostname}`;
                      window.location.reload();
                    } else {
                      // Reset to English
                      document.cookie = 'googtrans=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT';
                      document.cookie = `googtrans=;path=/;domain=${window.location.hostname};expires=Thu, 01 Jan 1970 00:00:00 GMT`;
                      window.location.reload();
                    }
                  }}
                  defaultValue=""
                >
                  <option value="">Language</option>
                  <option value="en">English</option>
                  <option value="zh-CN">中文</option>
                  <option value="zh-TW">繁體中文</option>
                  <option value="fr">Français</option>
                  <option value="es">Español</option>
                  <option value="de">Deutsch</option>
                  <option value="ja">日本語</option>
                  <option value="ko">한국어</option>
                  <option value="ar">العربية</option>
                  <option value="hi">हिन्दी</option>
                  <option value="pt">Português</option>
                  <option value="ru">Русский</option>
                  <option value="it">Italiano</option>
                </select>
              </div>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </div>
  );
};
